import { defineStore } from 'pinia'
import logger from '@/utils/logger'

export type ThemeType = 'default' | 'openclaw' | 'openclaw-light' | 'dark' | 'cool' | 'warm'

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    theme: (localStorage.getItem('theme') as ThemeType) || 'warm',
    // 温度主题（cool-冷色调，warm-暖色调）
    themeColor: (localStorage.getItem('themeColor') as 'cool' | 'warm') || 'warm',
    // 暗黑模式支持
    isDarkMode: localStorage.getItem('isDarkMode') === 'true',
    // 主题模式（light-浅色，dark-深色）
    themeMode: (localStorage.getItem('themeMode') as 'light' | 'dark') || 'light'
  }),
  
  getters: {
    currentTheme: (state) => state.theme,
    isOpenClawTheme: (state) => state.theme === 'openclaw',
    currentIsDark: (state) => state.isDarkMode
  },
  
  actions: {
    setTheme(theme: ThemeType) {
      this.theme = theme
      localStorage.setItem('theme', theme)
      this.applyTheme(theme)
    },

    /**
     * 设置温度主题（冷色调/暖色调）
     */
    setThemeColor(color: 'cool' | 'warm') {
      this.themeColor = color
      localStorage.setItem('themeColor', color)
      this.applyThemeColor(color)
      // 同步到后端
      this.syncThemeToBackend()
    },

    /**
     * 应用温度主题
     */
    applyThemeColor(color: 'cool' | 'warm') {
      const html = document.documentElement

      // 设置data-theme属性，激活对应的CSS样式
      // CSS文件已在main.ts中引入，会根据[data-theme]属性自动应用
      html.setAttribute('data-theme', color)

      // 移除可能存在的其他主题类
      html.classList.remove('theme-cool', 'theme-warm')

      // 添加当前主题类（备用）
      html.classList.add(`theme-${color}`)

      logger.debug('[Settings] 应用温度主题:', color, 'data-theme:', html.getAttribute('data-theme'))
    },

    /**
     * 同步主题设置到后端
     */
    async syncThemeToBackend() {
      // TODO: 实现后端API调用
      // import { userApi } from '@/api/user'
      // await userApi.updateTheme({
      //   themeColor: this.themeColor,
      //   themeMode: this.themeMode
      // })
    },
    
    /**
     * 切换暗黑模式
     */
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode
      localStorage.setItem('isDarkMode', String(this.isDarkMode))
      this.applyDarkMode()
    },
    
    /**
     * 设置暗黑模式
     */
    setDarkMode(isDark: boolean) {
      this.isDarkMode = isDark
      localStorage.setItem('isDarkMode', String(isDark))
      this.applyDarkMode()
    },
    
    /**
     * 应用暗黑模式CSS变量
     */
    applyDarkMode() {
      const html = document.documentElement
      
      if (this.isDarkMode) {
        // 暗黑模式变量
        html.classList.add('dark')
        html.setAttribute('data-theme', 'dark')
        
        // 设置暗黑模式CSS变量
        html.style.setProperty('--color-bg-primary', '#1a1a2e')
        html.style.setProperty('--color-bg-secondary', '#16213e')
        html.style.setProperty('--color-bg-tertiary', '#0f3460')
        html.style.setProperty('--color-text-primary', '#eaeaea')
        html.style.setProperty('--color-text-secondary', '#b0b0b0')
        html.style.setProperty('--color-text-placeholder', '#707070')
        html.style.setProperty('--color-border-base', '#3d3d5c')
        html.style.setProperty('--color-border-light', '#2d2d4a')
        html.style.setProperty('--color-border-lighter', '#252545')
        
        // Element Plus暗黑模式
        html.style.setProperty('--el-bg-color', '#1a1a2e')
        html.style.setProperty('--el-bg-color-page', '#0f0f1a')
        html.style.setProperty('--el-bg-color-overlay', '#1a1a2e')
        html.style.setProperty('--el-text-color-primary', '#eaeaea')
        html.style.setProperty('--el-text-color-regular', '#b0b0b0')
        html.style.setProperty('--el-text-color-secondary', '#707070')
        html.style.setProperty('--el-border-color', '#3d3d5c')
        html.style.setProperty('--el-border-color-light', '#2d2d4a')
        html.style.setProperty('--el-fill-color-blank', '#1a1a2e')
      } else {
        // 恢复亮色模式 - 不要移除 data-theme，让 applyTheme 控制它
        html.classList.remove('dark')
        // 只在当前是 dark 主题时才移除 data-theme
        if (html.getAttribute('data-theme') === 'dark') {
          html.setAttribute('data-theme', this.theme)
        }
        
        // 恢复默认CSS变量
        html.style.removeProperty('--color-bg-primary')
        html.style.removeProperty('--color-bg-secondary')
        html.style.removeProperty('--color-bg-tertiary')
        html.style.removeProperty('--color-text-primary')
        html.style.removeProperty('--color-text-secondary')
        html.style.removeProperty('--color-text-placeholder')
        html.style.removeProperty('--color-border-base')
        html.style.removeProperty('--color-border-light')
        html.style.removeProperty('--color-border-lighter')
        
        // Element Plus亮色模式
        html.style.removeProperty('--el-bg-color')
        html.style.removeProperty('--el-bg-color-page')
        html.style.removeProperty('--el-bg-color-overlay')
        html.style.removeProperty('--el-text-color-primary')
        html.style.removeProperty('--el-text-color-regular')
        html.style.removeProperty('--el-text-color-secondary')
        html.style.removeProperty('--el-border-color')
        html.style.removeProperty('--el-border-color-light')
        html.style.removeProperty('--el-fill-color-blank')
      }
    },
    
    applyTheme(theme: ThemeType) {
      document.documentElement.setAttribute('data-theme', theme)
    },
    
    initTheme() {
      logger.debug('[Settings] 初始化主题系统')

      // 初始化基础主题
      const savedTheme = localStorage.getItem('theme') as ThemeType
      if (savedTheme) {
        this.theme = savedTheme
      } else {
        this.theme = 'warm'
        localStorage.setItem('theme', 'warm')
      }
      this.applyTheme(this.theme)

      // 初始化温度主题（cool/warm）
      const savedThemeColor = localStorage.getItem('themeColor') as 'cool' | 'warm'
      if (savedThemeColor) {
        this.themeColor = savedThemeColor
      } else {
        // 默认使用暖色调
        this.themeColor = 'warm'
        localStorage.setItem('themeColor', 'warm')
      }
      this.applyThemeColor(this.themeColor)

      // 初始化暗黑模式
      const savedDarkMode = localStorage.getItem('isDarkMode')
      if (savedDarkMode !== null) {
        this.isDarkMode = savedDarkMode === 'true'
      } else {
        // 检查系统偏好
        this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
      }
      this.applyDarkMode()

      logger.debug('[Settings] 主题初始化完成:', {
        theme: this.theme,
        themeColor: this.themeColor,
        isDarkMode: this.isDarkMode
      })

      // 监听系统主题变化
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (localStorage.getItem('isDarkMode') === null) {
          // 只有用户没有手动设置时才跟随系统
          this.isDarkMode = e.matches
          this.applyDarkMode()
        }
      })
    }
  }
})
